import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Dropdown, Card, Row, Col, Spinner } from 'react-bootstrap';

//Import Components
import { ThemeContext } from "../../../context/ThemeContext";
import axiosInstance from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

// Import new dashboard components
import RevenueChart from './Dashboard/RevenueChart';
import ExpenseChart from './Dashboard/ExpenseChart';
import CashFlowChart from './Dashboard/CashFlowChart';
import TopCustomers from './Dashboard/TopCustomers';
import RecentTransactions from './Dashboard/RecentTransactions';
import UpcomingPayments from './Dashboard/UpcomingPayments.jsx';

const Home = () => {
    const { companyId } = useParams();
    const { changeBackground } = useContext(ThemeContext);
	const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        overview: {
            totalRevenue: 0,
            totalExpenses: 0,
            netProfit: 0,
            cashFlow: 0
        },
        invoices: {
            sent: 0,
            paid: 0,
            unpaid: 0,
            overdue: 0,
            totalOutstanding: 0
        },
        quotes: {
            total: 0,
            pending: 0,
            accepted: 0,
            rejected: 0
		},
		bankAccounts: {
			accounts: [
				{
					accountNumber: '1930',
					accountName: 'Checkkonto Swedbank',
					balance: 0,
					bankName: 'Swedbank',
					type: 'checking',
					lastUpdated: '2025-06-05T10:30:00',
					isConnected: false
				},
				{
					accountNumber: '1940',
					accountName: 'Sparkonto SEB',
					balance: 0,
					bankName: 'SEB',
					type: 'savings',
					lastUpdated: '2025-06-04T16:20:00',
					isConnected: false
				},
				{
					accountNumber: '1920',
					accountName: 'Företagskonto Nordea',
					balance: 0,
					bankName: 'Nordea',
					type: 'business',
					lastUpdated: '2025-06-05T09:15:00',
					isConnected: true
				},
				{
					accountNumber: '1950',
					accountName: 'Investeringskonto Handelsbanken',
					balance: 0,
					bankName: 'Handelsbanken',
					type: 'investment',
					lastUpdated: '2025-06-03T14:45:00',
					isConnected: false
				}
			],
			totalBalance: 0
		},
        recentActivity: []
	});
	
	const [selectedPeriod, setSelectedPeriod] = useState('current_fiscal_year');
	const [customStartDate, setCustomStartDate] = useState('');
	const [customEndDate, setCustomEndDate] = useState('');
	const [error, setError] = useState('');

	const [cashFlowPeriod, setCashFlowPeriod] = useState('last_12_months');
	const [cashFlowAnalysis, setCashFlowAnalysis] = useState('balance');

    useEffect(() => {
        changeBackground({ value: "light", label: "Light" });
        fetchDashboardData();
    }, [companyId]);

    const fetchDashboardData = async () => {
		setLoading(true);
		

		try {
			const period = selectedPeriod || 'current_fiscal_year';
			let getDashboardOverviewApiUrs = getApiUrl(`${companyId}/dashboard/overview?period=${period}`);

			if (period === 'custom' && customStartDate && customEndDate) {
				getDashboardOverviewApiUrs += `&startDate=${customStartDate}&endDate=${customEndDate}`;
			}

			const response = await axiosInstance.get(getDashboardOverviewApiUrs);

			console.log('Dashboard data response:', response.data);

			if (response.status === 200) {
				setDashboardData(response.data);
			} else {
				console.error('Failed to fetch dashboard data:', response.statusText);
			}

            // Use dummy data for now
            /*setDashboardData({
                overview: {
                    totalRevenue: 1250000,
                    totalExpenses: 890000,
                    netProfit: 360000,
                    cashFlow: 180000
                },
                invoices: {
                    sent: 24,
                    paid: 18,
                    unpaid: 6,
                    overdue: 2,
                    totalOutstanding: 85000
                },
                quotes: {
                    total: 12,
                    pending: 5,
                    accepted: 6,
                    rejected: 1
				},
				bankAccounts: {
					accounts: [
						{
							accountNumber: '1930',
							accountName: 'Checkkonto Swedbank',
							balance: 245780,
							bankName: 'Swedbank',
							type: 'checking',
							lastUpdated: '2025-06-05T10:30:00',
							isConnected: false
						},
						{
							accountNumber: '1940',
							accountName: 'Sparkonto SEB',
							balance: 125000,
							bankName: 'SEB',
							type: 'savings',
							lastUpdated: '2025-06-04T16:20:00',
							isConnected: false
						},
						{
							accountNumber: '1920',
							accountName: 'Företagskonto Nordea',
							balance: 89340,
							bankName: 'Nordea',
							type: 'business',
							lastUpdated: '2025-06-05T09:15:00',
							isConnected: true
						},
						{
							accountNumber: '1950',
							accountName: 'Investeringskonto Handelsbanken',
							balance: 456200,
							bankName: 'Handelsbanken',
							type: 'investment',
							lastUpdated: '2025-06-03T14:45:00',
							isConnected: false
						}
					],
					totalBalance: 916320
				},
                recentActivity: []
            });
			*/
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
	};
	
	const getBankIcon = (bankName) => {
    	const icons = {
        	'Swedbank': (
            	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                	<path d="M3 21H21V19H3V21ZM5 10H19C19.55 10 20 9.55 20 9S19.55 8 19 8H5C4.45 8 4 8.45 4 9S4.45 10 5 10ZM3 3V5H21V3H3ZM8 14H16C16.55 14 17 13.55 17 13S16.55 12 16 12H8C7.45 12 7 12.45 7 13S7.45 14 8 14ZM11 18H13C13.55 18 14 17.55 14 17S13.55 16 13 16H11C10.45 16 10 16.45 10 17S10.45 18 11 18Z" fill="#ff6600"/>
            	</svg>
        	),
        	'SEB': (
            	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                	<path d="M12 2L22 7V17C22 18.1 21.1 19 20 19H4C2.9 19 2 18.1 2 17V7L12 2ZM12 4.2L4 8.2V17H20V8.2L12 4.2ZM12 8C13.66 8 15 9.34 15 11S13.66 14 12 14 9 12.66 9 11 10.34 8 12 8ZM12 10C11.45 10 11 10.45 11 11S11.45 12 12 12 13 11.55 13 11 12.55 10 12 10Z" fill="#00a651"/>
            	</svg>
        	),
        	'Nordea': (
            	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                	<path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 7H7V9H17V7ZM17 11H7V13H17V11ZM14 15H7V17H14V15Z" fill="#0f4c8c"/>
            	</svg>
        	),
			'Handelsbanken': (
            	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                	<path d="M12 2L1 9L4 9V20H20V9L23 9L12 2ZM12 4.69L18 9H6L12 4.69ZM6 11H8V18H6V11ZM10 11H12V18H10V11ZM14 11H16V18H14V11ZM18 11H18V18H18V11Z" fill="#003776"/>
            	</svg>
        	)
    	};
    	
		return icons[bankName] || (
        	<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            	<path d="M12 2L1 9L4 9V20H20V9L23 9L12 2ZM12 4.69L18 9H6L12 4.69ZM6 11H8V18H6V11ZM10 11H12V18H10V11ZM14 11H16V18H14V11ZM18 11H18V18H18V11Z" fill="#64748b"/>
        	</svg>
    	);
	};

	const getAccountTypeColor = (type) => {
		const colors = {
			'checking': '#3b82f6',
			'savings': '#10b981',
			'business': '#f59e0b',
			'investment': '#8b5cf6'
		};
		return colors[type] || '#64748b';
	};

	const getAccountTypeLabel = (type) => {
		const labels = {
			'checking': 'Checkkonto',
			'savings': 'Sparkonto',
			'business': 'Företagskonto',
			'investment': 'Investeringskonto'
		};
		return labels[type] || type;
	};

	const formatLastUpdated = (dateString) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

		if (diffInHours < 1) return 'Nyligen uppdaterad';
		if (diffInHours < 24) return `${diffInHours}h sedan`;

		const diffInDays = Math.floor(diffInHours / 24);
		return `${diffInDays} dag${diffInDays > 1 ? 'ar' : ''} sedan`;
	};

    if (loading) {
        return (
            <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                <style>{`
                    .loading-container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        height: 400px;
                        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                        border-radius: 16px;
                        border: 1px solid #e2e8f0;
                    }
                    
                    .loading-spinner {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 1rem;
                    }
                    
                    .spinner-custom {
                        width: 48px;
                        height: 48px;
                        border: 4px solid #e2e8f0;
                        border-top: 4px solid #3b82f6;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
                <div className="loading-container">
                    <div className="loading-spinner">
                        <div className="spinner-custom"></div>
                        <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Laddar dashboard...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="" >
            <style>{`
                .dashboard-section {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    transition: all 0.3s ease;
                }
                
                .dashboard-section:hover {
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
                    transform: translateY(-2px);
                }
                
                .metric-card {
                    background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
                    border-radius: 16px;
                    padding: 2rem;
                    border: 1px solid #e2e8f0;
                    height: 100%;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .metric-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: var(--accent-gradient);
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                    transform-origin: left;
                }
                
                .metric-card:hover::before {
                    transform: scaleX(1);
                }
                
                .metric-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
                }
                
                .metric-card.revenue {
                    --accent-gradient: linear-gradient(90deg, #10b981 0%, #059669 100%);
                }
                
                .metric-card.expense {
                    --accent-gradient: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
                }
                
                .metric-card.profit {
                    --accent-gradient: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
                }
                
                .metric-card.cashflow {
                    --accent-gradient: linear-gradient(90deg, #06b6d4 0%, #0891b2 100%);
                }
                
                .metric-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                    transition: all 0.3s ease;
                }
                
                .metric-card:hover .metric-icon {
                    transform: scale(1.1);
                }
                
                .metric-value {
                    font-size: 2rem;
                    font-weight: 700;
                    line-height: 1.2;
                    margin-bottom: 0.5rem;
                    letter-spacing: -0.025em;
                }
                
                .metric-label {
                    font-size: 0.875rem;
                    color: #64748b;
                    font-weight: 500;
                    margin-bottom: 1rem;
                }
                
                .metric-change {
                    font-size: 0.75rem;
                    font-weight: 600;
                    padding: 0.25rem 0.75rem;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                }
                
                .metric-change.positive {
                    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
                    color: #166534;
                }
                
                .metric-change.negative {
                    background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
                    color: #dc2626;
                }
                
                .status-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }
                
                .status-item {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    border-radius: 12px;
                    padding: 1.5rem;
                    text-align: center;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                }
                
                .status-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
                }
                
                .status-value {
                    font-size: 1.75rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    line-height: 1;
                }
                
                .status-label {
                    font-size: 0.875rem;
                    color: #64748b;
                    font-weight: 500;
                }
                
                .chart-container {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    height: 100%;
                }
                
                .chart-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                
                .chart-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }
                
                .quick-actions {
                    display: grid;
                    gap: 1rem;
                }
                
                .action-button {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem 1.5rem;
                    background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    text-decoration: none;
                    color: #374151;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .action-button::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
                    transition: left 0.5s ease;
                }
                
                .action-button:hover::before {
                    left: 100%;
                }
                
                .action-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.15);
                    border-color: var(--bs-primary);
                    color: var(--bs-primary);
                    text-decoration: none;
                }
                
                .action-icon {
                    width: 20px;
                    height: 20px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .summary-card {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    border-radius: 12px;
                    padding: 1.5rem;
                    border: 1px solid #e2e8f0;
                    margin-top: 2rem;
                }
                
                .summary-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 0;
                }
                
                .summary-row:not(:last-child) {
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .summary-label {
                    color: #64748b;
                    font-size: 0.875rem;
                    font-weight: 500;
                }
                
                .summary-value {
                    font-weight: 600;
                    font-size: 0.875rem;
                }

				.bank-account-card {
                    background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
                    border-radius: 12px;
                    padding: 1.5rem;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 1rem;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .bank-account-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: var(--account-color);
                    transform: scaleX(0);
                    transition: transform 0.3s ease;
                    transform-origin: left;
                }
                
                .bank-account-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
                }
                
                .bank-account-card:hover::before {
                    transform: scaleX(1);
                }
                
                .bank-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1rem;
                }
                
                .bank-info {
                    flex: 1;
                }
                
                .bank-name {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 0.25rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .account-number {
                    font-size: 0.875rem;
                    color: #64748b;
                    font-family: 'Monaco', 'Menlo', monospace;
                }
                
                .bank-balance {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }
                
                .account-meta {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.75rem;
                    color: #64748b;
                }
                
                .account-type-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.25rem 0.75rem;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: white;
                }
                
                .connection-status {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    font-size: 0.75rem;
                }
                
                .status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                }
                
                .status-connected {
                    background: #10b981;
                }
                
                .status-disconnected {
                    background: #f59e0b;
                }
                
                .total-balance-card {
                    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
                    border-radius: 16px;
                    padding: 2rem;
                    color: white;
                    margin-bottom: 1rem;
                    position: relative;
                    overflow: hidden;
                }
                
                .total-balance-card::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -50%;
                    width: 100%;
                    height: 200%;
                    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.05), transparent);
                    transform: rotate(45deg);
                    transition: all 0.5s ease;
                }
                
                .total-balance-card:hover::before {
                    animation: shimmer 1.5s ease-in-out;
                }
                
                @keyframes shimmer {
                    0% { transform: translateX(-100%) rotate(45deg); }
                    100% { transform: translateX(100%) rotate(45deg); }
                }
                
                .total-balance-title {
                    font-size: 1rem;
                    opacity: 0.8;
                    margin-bottom: 0.5rem;
                }
                
                .total-balance-amount {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin-bottom: 1rem;
                }
                
                .balance-breakdown {
                    display: flex;
                    justify-content: space-between;
                    opacity: 0.8;
                    font-size: 0.875rem;
                }
            `}</style>

            {/* Financial Overview Cards */}
            <Row className="mb-4">
                <Col xl={3} sm={6} className="mb-3">
                    <div className="metric-card revenue">
                        <div className="metric-icon" style={{
                            background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
                                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="metric-label">Total intäkter</div>
                        <div className="metric-value" style={{ color: '#059669' }}>
                            {formatCurrency(dashboardData.overview.totalRevenue)}
                        </div>
                       {/* <div className="metric-change positive">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            +8.2% från förra månaden
                        </div>*/}
                    </div>
                </Col>

                <Col xl={3} sm={6} className="mb-3">
                    <div className="metric-card expense">
                        <div className="metric-icon" style={{
                            background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)'
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
                                <path d="M17 14L12 19L7 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="metric-label">Total kostnader</div>
                        <div className="metric-value" style={{ color: '#dc2626' }}>
                            {formatCurrency(dashboardData.overview.totalExpenses)}
                        </div>
                            {/*<div className="metric-change negative">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            +3.1% från förra månaden
                        </div>}*/}
                    </div>
                </Col>

                <Col xl={3} sm={6} className="mb-3">
                    <div className="metric-card profit">
                        <div className="metric-icon" style={{
                            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
                                <path d="M16 21V5C16 4.44772 15.5523 4 15 4H9C8.44772 4 8 4.44772 8 5V21L12 17L16 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="metric-label">Nettovinst</div>
                        <div className="metric-value" style={{ color: '#1d4ed8' }}>
                            {formatCurrency(dashboardData.overview.netProfit)}
                        </div>
                        {/*<div className="metric-change positive">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            +12.4% från förra månaden
                        </div>*/}
                    </div>
                </Col>

                <Col xl={3} sm={6} className="mb-3">
                    <div className="metric-card cashflow">
                        <div className="metric-icon" style={{
                            background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)'
                        }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#0891b2' }}>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="metric-label">Kassaflöde</div>
                        <div className="metric-value" style={{ color: '#0891b2' }}>
                            {formatCurrency(dashboardData.overview.cashFlow)}
                        </div>
                        {/*<div className="metric-change positive">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            +5.7% från förra månaden
                        </div>*/}
                    </div>
                </Col>
            </Row>

            {/* Invoice & Quote Status */}
            <Row className="mb-4">
                <Col xl={6}>
                    <div className="dashboard-section">
                        <div className="d-flex align-items-center mb-4">
                            <div className="metric-icon me-3" style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                width: '48px',
                                height: '48px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
                                    <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Fakturor</h5>
                        </div>

                        <div className="status-grid">
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#3b82f6' }}>{dashboardData.invoices.sent}</div>
                                <div className="status-label">Skickade</div>
                            </div>
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#10b981' }}>{dashboardData.invoices.paid}</div>
                                <div className="status-label">Betalda</div>
                            </div>
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#f59e0b' }}>{dashboardData.invoices.unpaid}</div>
                                <div className="status-label">Obetalda</div>
                            </div>
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#ef4444' }}>{dashboardData.invoices.overdue}</div>
                                <div className="status-label">Förfallna</div>
                            </div>
                        </div>

                        <div className="summary-card">
                            <div className="summary-row">
                                <span className="summary-label">Utestående belopp:</span>
                                <strong className="summary-value" style={{ color: '#f59e0b' }}>
                                    {formatCurrency(dashboardData.invoices.totalOutstanding)}
                                </strong>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={6}>
                    <div className="dashboard-section">
                        <div className="d-flex align-items-center mb-4">
                            <div className="metric-icon me-3" style={{
                                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                width: '48px',
                                height: '48px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
                                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            </div>
                            <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Offerter</h5>
                        </div>

                        <div className="status-grid">
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#3b82f6' }}>{dashboardData.quotes.total}</div>
                                <div className="status-label">Totalt</div>
                            </div>
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#f59e0b' }}>{dashboardData.quotes.pending}</div>
                                <div className="status-label">Väntande</div>
                            </div>
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#10b981' }}>{dashboardData.quotes.accepted}</div>
                                <div className="status-label">Accepterade</div>
                            </div>
                            <div className="status-item">
                                <div className="status-value" style={{ color: '#ef4444' }}>{dashboardData.quotes.rejected}</div>
                                <div className="status-label">Avslagna</div>
                            </div>
                        </div>

                        <div className="summary-card">
                            <div className="summary-row">
                                <span className="summary-label">Konverteringsgrad:</span>
                                <strong className="summary-value" style={{ color: '#10b981' }}>
                                    {dashboardData.quotes.total > 0 ?
                                        Math.round((dashboardData.quotes.accepted / dashboardData.quotes.total) * 100) : 0}%
                                </strong>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Charts Row */}
            <Row className="mb-4">
                <Col xl={8}>
                    <div className="chart-container">
                        <div className="chart-header">
                            <h5 className="chart-title">Intäkter vs Kostnader</h5>
                            
                        </div>
                        <RevenueChart />
                    </div>
                </Col>

                <Col xl={4}>
                    <div className="chart-container">
                        <div className="chart-header">
                            <h5 className="chart-title">Kostnadsfördelning</h5>
                        </div>
                        <ExpenseChart />
                    </div>
                </Col>
            </Row>

            {/* Cash Flow Chart Row 
			<Row className="mb-4">
				<Col xl={12}>
					<div className="chart-container">
						<div className="chart-header">
							<h5 className="chart-title">Kassaflöde över tid</h5>
							<div style={{ display: 'flex', gap: '0.5rem' }}>
								<Dropdown>
									<Dropdown.Toggle variant="outline-secondary" size="sm">
										{cashFlowPeriod === 'last_3_months' ? 'Senaste 3 månaderna' :
											cashFlowPeriod === 'last_6_months' ? 'Senaste 6 månaderna' :
												cashFlowPeriod === 'last_12_months' ? 'Senaste 12 månaderna' :
													cashFlowPeriod === 'last_24_months' ? 'Senaste 24 månaderna' :
														cashFlowPeriod === 'last_90_days' ? 'Senaste 90 dagarna' :
															cashFlowPeriod === 'last_30_days' ? 'Senaste 30 dagarna' :
																'Detta år'}
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_30_days')}>
											Senaste 30 dagarna
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_90_days')}>
											Senaste 90 dagarna
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_3_months')}>
											Senaste 3 månaderna
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_6_months')}>
											Senaste 6 månaderna
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_12_months')}>
											Senaste 12 månaderna
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_24_months')}>
											Senaste 24 månaderna
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('this_year')}>
											Detta år
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowPeriod('last_year')}>
											Förra året
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>

								<Dropdown>
									<Dropdown.Toggle variant="outline-primary" size="sm">
										{cashFlowAnalysis === 'balance' ? 'Kassasaldo' :
											cashFlowAnalysis === 'flows' ? 'Kassaflödesanalys' :
												'In- & Utflöde'}
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Dropdown.Item onClick={() => setCashFlowAnalysis('balance')}>
											Kassasaldo
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowAnalysis('flows')}>
											Kassaflödesanalys
										</Dropdown.Item>
										<Dropdown.Item onClick={() => setCashFlowAnalysis('inflow_outflow')}>
											In- & Utflöde
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
						<CashFlowChart period={cashFlowPeriod} analysis={cashFlowAnalysis} />
					</div>
				</Col>
			</Row>

            */}

            {/* Recent Activity Row */}
            <Row className="mb-4">
                <Col xl={6}>
                    <div className="chart-container">
                        <div className="chart-header">
                            <h5 className="chart-title">Senaste aktivitet</h5>
                            <Link
                                to={`/${companyId}/verifications`}
                                className="action-button"
                                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                            >
                                Visa alla transaktioner
                            </Link>
                        </div>
                        <RecentTransactions />
                    </div>
                </Col>

                <Col xl={6}>
                    <div className="chart-container">
                        <div className="chart-header">
                            <h5 className="chart-title">Toppkunder</h5>
                        </div>
                        <TopCustomers />
                    </div>
                </Col>
            </Row>

            {/* Upcoming Payments & Quick Actions Row */}
            <Row className="mb-4">
                <Col xl={8}>
                    <div className="dashboard-section">
                        <div className="d-flex align-items-center mb-4">
                            <div className="metric-icon me-3" style={{
                                background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                                width: '48px',
                                height: '48px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Kommande förfall</h5>
                            <div className="ms-auto">
                                <Link
                                    to={`/${companyId}/invoices`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem 1rem',
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                        color: 'white',
                                        textDecoration: 'none',
                                        borderRadius: '8px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    Hantera alla betalningar
                                </Link>
                            </div>
                        </div>
                        <UpcomingPayments />
                    </div>
                </Col>

                <Col xl={4}>
                    <div className="dashboard-section">
                        <div className="d-flex align-items-center mb-4">
                            <div className="metric-icon me-3" style={{
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                width: '48px',
                                height: '48px'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
                                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Snabbåtgärder</h5>
                        </div>

                        <div className="quick-actions">
                            <Link to={`/${companyId}/create-invoices`} className="action-button">
                                <div className="action-icon" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
                                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                Skapa faktura
                            </Link>
                            <Link to={`/${companyId}/create-quote`} className="action-button">
                                <div className="action-icon" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
                                        <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                Skapa offert
                            </Link>
                            <Link to={`/${companyId}/create-bookkeeping-entry`} className="action-button">
                                <div className="action-icon" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
                                        <path d="M4 19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                Bokför transaktion
                            </Link>
                            <Link to={`/${companyId}/customers`} className="action-button">
                                <div className="action-icon" style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#4338ca' }}>
                                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                                    </svg>
                                </div>
                                Hantera kunder
                            </Link>
                        </div>

                        {/* Performance Summary 
                        <div style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            border: '1px solid #e2e8f0',
                            marginTop: '2rem'
                        }}>
                            <h6 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '1rem' }}>
                                Månadens sammanfattning
                            </h6>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Nya kunder:</span>
                                    <span style={{ fontWeight: '600', color: '#10b981' }}>+3</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Skickade fakturor:</span>
                                    <span style={{ fontWeight: '600', color: '#3b82f6' }}>12</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Betalda fakturor:</span>
                                    <span style={{ fontWeight: '600', color: '#10b981' }}>8</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Genomsnittlig betalningstid:</span>
                                    <span style={{ fontWeight: '600', color: '#f59e0b' }}>14 dagar</span>
                                </div>
                            </div>
                        </div>*/}
                    </div>
                </Col>
			</Row>
			
			{/* Bank Accounts Section */}
			<Row className="mb-4">
				<Col xl={8}>
					<div className="dashboard-section">
						<div className="d-flex align-items-center mb-4">
							<div className="metric-icon me-3" style={{
								background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
								width: '48px',
								height: '48px'
							}}>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
									<rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
									<path d="M7 15h0M2 10h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
								</svg>
							</div>
							<h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Bankkonton</h5>
							<div className="ms-auto">
								<Link
									to={`/${companyId}/bank-sync`}
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: '0.5rem',
										padding: '0.5rem 1rem',
										background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
										color: 'white',
										textDecoration: 'none',
										borderRadius: '8px',
										fontSize: '0.875rem',
										fontWeight: '500',
										transition: 'all 0.3s ease'
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.transform = 'translateY(-2px)';
										e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.transform = 'translateY(0)';
										e.currentTarget.style.boxShadow = 'none';
									}}
								>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
										<path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
									</svg>
									Synkronisera banker
								</Link>
							</div>
						</div>

						<div className="row">
							{dashboardData.bankAccounts.accounts.map((account, index) => (
								<div key={account.accountNumber} className="col-md-6 mb-3">
									<div
										className="bank-account-card"
										style={{ '--account-color': getAccountTypeColor(account.type) }}
									>
										<div className="bank-header">
											<div className="bank-info">
												<div className="bank-name">
													<span style={{
														fontSize: '1.2rem',
														display: 'flex',
														alignItems: 'center',
														marginRight: '0.5rem'
													}}>
														{getBankIcon(account.bankName)}
													</span>
													{account.accountName}
												</div>
												<div className="account-number">
													Konto: {account.accountNumber}
												</div>
											</div>
											<div className="connection-status">
												<div className={`status-dot ${account.isConnected ? 'status-connected' : 'status-disconnected'}`}></div>
												<span>{account.isConnected ? 'Ansluten' : 'Ej ansluten'}</span>
											</div>
										</div>

										<div className="bank-balance">
											{formatCurrency(account.balance)}
										</div>

										<div className="account-meta">
											<div
												className="account-type-badge"
												style={{ backgroundColor: getAccountTypeColor(account.type) }}
											>
												{getAccountTypeLabel(account.type)}
											</div>
											<span>{formatLastUpdated(account.lastUpdated)}</span>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</Col>

				<Col xl={4}>
					<div className="dashboard-section">
						<div className="total-balance-card">
							<div className="total-balance-title">Total tillgängligt saldo</div>
							<div className="total-balance-amount">
								{formatCurrency(dashboardData.bankAccounts.totalBalance)}
							</div>
							<div className="balance-breakdown">
								<span>{dashboardData.bankAccounts.accounts.filter(acc => acc.isConnected).length} anslutna konton</span>
								<span>{dashboardData.bankAccounts.accounts.length} totalt</span>
							</div>
						</div>

						{/* Quick Bank Actions */}
						<div style={{ marginTop: '1rem' }}>
							<h6 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '1rem' }}>
								Bankåtgärder
							</h6>
							<div style={{ display: 'grid', gap: '0.75rem' }}>
								<Link to={`/${companyId}/transactions/import`} className="action-button">
									<div className="action-icon" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
											<path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" />
											<path d="M14 2V8H20M12 18V12M9 15L12 12L15 15" stroke="currentColor" strokeWidth="2" />
										</svg>
									</div>
									Importera transaktioner
								</Link>
								<Link to={`/${companyId}/bank-reconciliation`} className="action-button">
									<div className="action-icon" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }}>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
											<path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
										</svg>
									</div>
									Bankavstämning
								</Link>
								<Link to={`/${companyId}/accounts`} className="action-button">
									<div className="action-icon" style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)' }}>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#4338ca' }}>
											<path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" />
										</svg>
									</div>
									Hantera kontoplan
								</Link>
							</div>
						</div>
					</div>
				</Col>
			</Row>
        </div>
    );
};

export default Home;




// import React, { useContext, useEffect, useState } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Dropdown, Card, Row, Col, Spinner } from 'react-bootstrap';

// //Import Components
// import { ThemeContext } from "../../../context/ThemeContext";
// import axiosInstance from '../../../services/AxiosInstance';
// import { getApiUrl } from '../../../utils/ApiUtils';

// // Import new dashboard components
// import RevenueChart from './Dashboard/RevenueChart';
// import ExpenseChart from './Dashboard/ExpenseChart';
// import CashFlowChart from './Dashboard/CashFlowChart';
// import TopCustomers from './Dashboard/TopCustomers';
// import RecentTransactions from './Dashboard/RecentTransactions';
// import UpcomingPayments from './Dashboard/UpcomingPayments';

// const Home = () => {
//     const { companyId } = useParams();
//     const { changeBackground } = useContext(ThemeContext);
//     const [loading, setLoading] = useState(true);
//     const [dashboardData, setDashboardData] = useState({
//         overview: {
//             totalRevenue: 1250000,
//             totalExpenses: 890000,
//             netProfit: 360000,
//             cashFlow: 180000
//         },
//         invoices: {
//             sent: 24,
//             paid: 18,
//             unpaid: 6,
//             overdue: 2,
//             totalOutstanding: 85000
//         },
//         quotes: {
//             total: 12,
//             pending: 5,
//             accepted: 6,
//             rejected: 1
//         },
//         recentActivity: []
//     });

//     useEffect(() => {
//         changeBackground({ value: "light", label: "Light" });
//         fetchDashboardData();
//     }, [companyId]);

//     const fetchDashboardData = async () => {
//         setLoading(true);
//         try {
//             // Use dummy data for now
//             setDashboardData({
//                 overview: {
//                     totalRevenue: 1250000,
//                     totalExpenses: 890000,
//                     netProfit: 360000,
//                     cashFlow: 180000
//                 },
//                 invoices: {
//                     sent: 24,
//                     paid: 18,
//                     unpaid: 6,
//                     overdue: 2,
//                     totalOutstanding: 85000
//                 },
//                 quotes: {
//                     total: 12,
//                     pending: 5,
//                     accepted: 6,
//                     rejected: 1
//                 },
//                 recentActivity: []
//             });
//         } catch (error) {
//             console.error('Error fetching dashboard data:', error);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const formatCurrency = (amount) => {
//         return new Intl.NumberFormat('sv-SE', {
//             style: 'currency',
//             currency: 'SEK',
//             minimumFractionDigits: 0,
//             maximumFractionDigits: 0
//         }).format(amount);
//     };

//     if (loading) {
//         return (
//             <div className="container-fluid" style={{ maxWidth: '1400px' }}>
//                 <style>{`
//                     .loading-container {
//                         display: flex;
//                         justify-content: center;
//                         align-items: center;
//                         height: 400px;
//                         background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
//                         border-radius: 16px;
//                         border: 1px solid #e2e8f0;
//                     }
                    
//                     .loading-spinner {
//                         display: flex;
//                         flex-direction: column;
//                         align-items: center;
//                         gap: 1rem;
//                     }
                    
//                     .spinner-custom {
//                         width: 48px;
//                         height: 48px;
//                         border: 4px solid #e2e8f0;
//                         border-top: 4px solid #3b82f6;
//                         border-radius: 50%;
//                         animation: spin 1s linear infinite;
//                     }
                    
//                     @keyframes spin {
//                         0% { transform: rotate(0deg); }
//                         100% { transform: rotate(360deg); }
//                     }
//                 `}</style>
//                 <div className="loading-container">
//                     <div className="loading-spinner">
//                         <div className="spinner-custom"></div>
//                         <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Laddar dashboard...</span>
//                     </div>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div className="container-fluid" style={{ maxWidth: '1400px' }}>
//             <style>{`
//                 .dashboard-section {
//                     background: white;
//                     border-radius: 16px;
//                     padding: 2rem;
//                     margin-bottom: 2rem;
//                     border: 1px solid #e2e8f0;
//                     box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
//                     transition: all 0.3s ease;
//                 }
                
//                 .dashboard-section:hover {
//                     box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
//                     transform: translateY(-2px);
//                 }
                
//                 .metric-card {
//                     background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
//                     border-radius: 16px;
//                     padding: 2rem;
//                     border: 1px solid #e2e8f0;
//                     height: 100%;
//                     transition: all 0.3s ease;
//                     position: relative;
//                     overflow: hidden;
//                 }
                
//                 .metric-card::before {
//                     content: '';
//                     position: absolute;
//                     top: 0;
//                     left: 0;
//                     right: 0;
//                     height: 4px;
//                     background: var(--accent-gradient);
//                     transform: scaleX(0);
//                     transition: transform 0.3s ease;
//                     transform-origin: left;
//                 }
                
//                 .metric-card:hover::before {
//                     transform: scaleX(1);
//                 }
                
//                 .metric-card:hover {
//                     transform: translateY(-4px);
//                     box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
//                 }
                
//                 .metric-card.revenue {
//                     --accent-gradient: linear-gradient(90deg, #10b981 0%, #059669 100%);
//                 }
                
//                 .metric-card.expense {
//                     --accent-gradient: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
//                 }
                
//                 .metric-card.profit {
//                     --accent-gradient: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
//                 }
                
//                 .metric-card.cashflow {
//                     --accent-gradient: linear-gradient(90deg, #06b6d4 0%, #0891b2 100%);
//                 }
                
//                 .metric-icon {
//                     width: 56px;
//                     height: 56px;
//                     border-radius: 16px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     margin-bottom: 1.5rem;
//                     transition: all 0.3s ease;
//                 }
                
//                 .metric-card:hover .metric-icon {
//                     transform: scale(1.1);
//                 }
                
//                 .metric-value {
//                     font-size: 2rem;
//                     font-weight: 700;
//                     line-height: 1.2;
//                     margin-bottom: 0.5rem;
//                     letter-spacing: -0.025em;
//                 }
                
//                 .metric-label {
//                     font-size: 0.875rem;
//                     color: #64748b;
//                     font-weight: 500;
//                     margin-bottom: 1rem;
//                 }
                
//                 .metric-change {
//                     font-size: 0.75rem;
//                     font-weight: 600;
//                     padding: 0.25rem 0.75rem;
//                     border-radius: 8px;
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 0.375rem;
//                 }
                
//                 .metric-change.positive {
//                     background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
//                     color: #166534;
//                 }
                
//                 .metric-change.negative {
//                     background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
//                     color: #dc2626;
//                 }
                
//                 .status-grid {
//                     display: grid;
//                     grid-template-columns: repeat(2, 1fr);
//                     gap: 1.5rem;
//                     margin-bottom: 2rem;
//                 }
                
//                 .status-item {
//                     background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
//                     border-radius: 12px;
//                     padding: 1.5rem;
//                     text-align: center;
//                     border: 1px solid #e2e8f0;
//                     transition: all 0.3s ease;
//                 }
                
//                 .status-item:hover {
//                     transform: translateY(-2px);
//                     box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
//                 }
                
//                 .status-value {
//                     font-size: 1.75rem;
//                     font-weight: 700;
//                     margin-bottom: 0.5rem;
//                     line-height: 1;
//                 }
                
//                 .status-label {
//                     font-size: 0.875rem;
//                     color: #64748b;
//                     font-weight: 500;
//                 }
                
//                 .chart-container {
//                     background: white;
//                     border-radius: 16px;
//                     padding: 2rem;
//                     border: 1px solid #e2e8f0;
//                     box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
//                     height: 100%;
//                 }
                
//                 .chart-header {
//                     display: flex;
//                     justify-content: between;
//                     align-items: center;
//                     margin-bottom: 2rem;
//                     padding-bottom: 1rem;
//                     border-bottom: 2px solid #f1f5f9;
//                 }
                
//                 .chart-title {
//                     font-size: 1.25rem;
//                     font-weight: 600;
//                     color: #1e293b;
//                     margin: 0;
//                 }
                
//                 .quick-actions {
//                     display: grid;
//                     gap: 1rem;
//                 }
                
//                 .action-button {
//                     display: flex;
//                     align-items: center;
//                     gap: 0.75rem;
//                     padding: 1rem 1.5rem;
//                     background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
//                     border: 1px solid #e2e8f0;
//                     border-radius: 12px;
//                     text-decoration: none;
//                     color: #374151;
//                     font-weight: 500;
//                     transition: all 0.3s ease;
//                     position: relative;
//                     overflow: hidden;
//                 }
                
//                 .action-button::before {
//                     content: '';
//                     position: absolute;
//                     top: 0;
//                     left: -100%;
//                     width: 100%;
//                     height: 100%;
//                     background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
//                     transition: left 0.5s ease;
//                 }
                
//                 .action-button:hover::before {
//                     left: 100%;
//                 }
                
//                 .action-button:hover {
//                     transform: translateY(-2px);
//                     box-shadow: 0 8px 20px rgba(59, 130, 246, 0.15);
//                     border-color: #3b82f6;
//                     color: #3b82f6;
//                     text-decoration: none;
//                 }
                
//                 .action-icon {
//                     width: 20px;
//                     height: 20px;
//                     border-radius: 6px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                 }
                
//                 .summary-card {
//                     background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
//                     border-radius: 12px;
//                     padding: 1.5rem;
//                     border: 1px solid #e2e8f0;
//                     margin-top: 2rem;
//                 }
                
//                 .summary-row {
//                     display: flex;
//                     justify-content: between;
//                     align-items: center;
//                     padding: 0.75rem 0;
//                 }
                
//                 .summary-row:not(:last-child) {
//                     border-bottom: 1px solid #e2e8f0;
//                 }
                
//                 .summary-label {
//                     color: #64748b;
//                     font-size: 0.875rem;
//                     font-weight: 500;
//                 }
                
//                 .summary-value {
//                     font-weight: 600;
//                     font-size: 0.875rem;
//                 }
//             `}</style>

//             {/* Financial Overview Cards */}
//             <Row className="mb-4">
//                 <Col xl={3} sm={6} className="mb-3">
//                     <div className="metric-card revenue">
//                         <div className="metric-icon" style={{
//                             background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
//                         }}>
//                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
//                                 <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                 <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                 <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                         </div>
//                         <div className="metric-label">Total intäkter</div>
//                         <div className="metric-value" style={{ color: '#059669' }}>
//                             {formatCurrency(dashboardData.overview.totalRevenue)}
//                         </div>
//                         <div className="metric-change positive">
//                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                                 <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                             +8.2% från förra månaden
//                         </div>
//                     </div>
//                 </Col>

//                 <Col xl={3} sm={6} className="mb-3">
//                     <div className="metric-card expense">
//                         <div className="metric-icon" style={{
//                             background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)'
//                         }}>
//                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
//                                 <path d="M17 14L12 19L7 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                 <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                         </div>
//                         <div className="metric-label">Total kostnader</div>
//                         <div className="metric-value" style={{ color: '#dc2626' }}>
//                             {formatCurrency(dashboardData.overview.totalExpenses)}
//                         </div>
//                         <div className="metric-change negative">
//                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                                 <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                             +3.1% från förra månaden
//                         </div>
//                     </div>
//                 </Col>

//                 <Col xl={3} sm={6} className="mb-3">
//                     <div className="metric-card profit">
//                         <div className="metric-icon" style={{
//                             background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
//                         }}>
//                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
//                                 <path d="M16 21V5C16 4.44772 15.5523 4 15 4H9C8.44772 4 8 4.44772 8 5V21L12 17L16 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                         </div>
//                         <div className="metric-label">Nettovinst</div>
//                         <div className="metric-value" style={{ color: '#1d4ed8' }}>
//                             {formatCurrency(dashboardData.overview.netProfit)}
//                         </div>
//                         <div className="metric-change positive">
//                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                                 <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                             +12.4% från förra månaden
//                         </div>
//                     </div>
//                 </Col>

//                 <Col xl={3} sm={6} className="mb-3">
//                     <div className="metric-card cashflow">
//                         <div className="metric-icon" style={{
//                             background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)'
//                         }}>
//                             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#0891b2' }}>
//                                 <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
//                                 <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                         </div>
//                         <div className="metric-label">Kassaflöde</div>
//                         <div className="metric-value" style={{ color: '#0891b2' }}>
//                             {formatCurrency(dashboardData.overview.cashFlow)}
//                         </div>
//                         <div className="metric-change positive">
//                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                                 <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             </svg>
//                             +5.7% från förra månaden
//                         </div>
//                     </div>
//                 </Col>
//             </Row>

//             {/* Invoice & Quote Status */}
//             <Row className="mb-4">
//                 <Col xl={6}>
//                     <div className="dashboard-section">
//                         <div className="d-flex align-items-center mb-4">
//                             <div className="metric-icon me-3" style={{
//                                 background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
//                                 width: '48px',
//                                 height: '48px'
//                             }}>
//                                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
//                                     <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                     <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                 </svg>
//                             </div>
//                             <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Fakturor</h5>
//                         </div>

//                         <div className="status-grid">
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#3b82f6' }}>{dashboardData.invoices.sent}</div>
//                                 <div className="status-label">Skickade</div>
//                             </div>
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#10b981' }}>{dashboardData.invoices.paid}</div>
//                                 <div className="status-label">Betalda</div>
//                             </div>
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#f59e0b' }}>{dashboardData.invoices.unpaid}</div>
//                                 <div className="status-label">Obetalda</div>
//                             </div>
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#ef4444' }}>{dashboardData.invoices.overdue}</div>
//                                 <div className="status-label">Förfallna</div>
//                             </div>
//                         </div>

//                         <div className="summary-card">
//                             <div className="summary-row">
//                                 <span className="summary-label">Utestående belopp:</span>
//                                 <strong className="summary-value" style={{ color: '#f59e0b' }}>
//                                     {formatCurrency(dashboardData.invoices.totalOutstanding)}
//                                 </strong>
//                             </div>
//                         </div>
//                     </div>
//                 </Col>

//                 <Col xl={6}>
//                     <div className="dashboard-section">
//                         <div className="d-flex align-items-center mb-4">
//                             <div className="metric-icon me-3" style={{
//                                 background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
//                                 width: '48px',
//                                 height: '48px'
//                             }}>
//                                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
//                                     <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                     <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
//                                 </svg>
//                             </div>
//                             <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Offerter</h5>
//                         </div>

//                         <div className="status-grid">
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#3b82f6' }}>{dashboardData.quotes.total}</div>
//                                 <div className="status-label">Totalt</div>
//                             </div>
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#f59e0b' }}>{dashboardData.quotes.pending}</div>
//                                 <div className="status-label">Väntande</div>
//                             </div>
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#10b981' }}>{dashboardData.quotes.accepted}</div>
//                                 <div className="status-label">Accepterade</div>
//                             </div>
//                             <div className="status-item">
//                                 <div className="status-value" style={{ color: '#ef4444' }}>{dashboardData.quotes.rejected}</div>
//                                 <div className="status-label">Avslagna</div>
//                             </div>
//                         </div>

//                         <div className="summary-card">
//                             <div className="summary-row">
//                                 <span className="summary-label">Konverteringsgrad:</span>
//                                 <strong className="summary-value" style={{ color: '#10b981' }}>
//                                     {dashboardData.quotes.total > 0 ?
//                                         Math.round((dashboardData.quotes.accepted / dashboardData.quotes.total) * 100) : 0}%
//                                 </strong>
//                             </div>
//                         </div>
//                     </div>
//                 </Col>
//             </Row>

//             {/* Charts Row */}
//             <Row className="mb-4">
//                 <Col xl={8}>
//                     <div className="chart-container">
//                         <div className="chart-header">
//                             <h5 className="chart-title">Intäkter vs Kostnader</h5>
//                             <Dropdown>
//                                 <Dropdown.Toggle
//                                     variant="outline-secondary"
//                                     size="sm"
//                                     style={{
//                                         borderRadius: '8px',
//                                         border: '1px solid #e2e8f0',
//                                         color: '#64748b'
//                                     }}
//                                 >
//                                     Senaste 12 månaderna
//                                 </Dropdown.Toggle>
//                                 <Dropdown.Menu>
//                                     <Dropdown.Item>Senaste 6 månaderna</Dropdown.Item>
//                                     <Dropdown.Item>Senaste 12 månaderna</Dropdown.Item>
//                                     <Dropdown.Item>Detta år</Dropdown.Item>
//                                 </Dropdown.Menu>
//                             </Dropdown>
//                         </div>
//                         <RevenueChart />
//                     </div>
//                 </Col>

//                 <Col xl={4}>
//                     <div className="chart-container">
//                         <div className="chart-header">
//                             <h5 className="chart-title">Kostnadsfördelning</h5>
//                         </div>
//                         <ExpenseChart />
//                     </div>
//                 </Col>
//             </Row>

//             {/* Cash Flow Chart Row */}
//             <Row className="mb-4">
//                 <Col xl={12}>
//                     <div className="chart-container">
//                         <div className="chart-header">
//                             <h5 className="chart-title">Kassaflöde över tid</h5>
//                             <Dropdown>
//                                 <Dropdown.Toggle
//                                     variant="outline-secondary"
//                                     size="sm"
//                                     style={{
//                                         borderRadius: '8px',
//                                         border: '1px solid #e2e8f0',
//                                         color: '#64748b'
//                                     }}
//                                 >
//                                     Senaste 12 månaderna
//                                 </Dropdown.Toggle>
//                                 <Dropdown.Menu>
//                                     <Dropdown.Item>Senaste 6 månaderna</Dropdown.Item>
//                                     <Dropdown.Item>Senaste 12 månaderna</Dropdown.Item>
//                                     <Dropdown.Item>Detta år</Dropdown.Item>
//                                 </Dropdown.Menu>
//                             </Dropdown>
//                         </div>
//                         <CashFlowChart />
//                     </div>
//                 </Col>
//             </Row>

//             {/* Recent Activity & Quick Actions */}
//             <Row>
//                 <Col xl={5}>
//                     <div className="chart-container mb-4">
//                         <div className="chart-header">
//                             <h5 className="chart-title">Senaste aktivitet</h5>
//                             <Link
//                                 to={`/${companyId}/verifications`}
//                                 className="action-button"
//                                 style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
//                             >
//                                 Visa alla transaktioner
//                             </Link>
//                         </div>
//                         <RecentTransactions />
//                     </div>
//                 </Col>

//                 <Col xl={4}>
//                     <div className="chart-container mb-4">
//                         <div className="chart-header">
//                             <h5 className="chart-title">Toppkunder</h5>
//                         </div>
//                         <TopCustomers />
//                     </div>
//                 </Col>

//                 <Col xl={3}>
//                     <div className="dashboard-section mb-4">
//                         <div className="d-flex align-items-center mb-4">
//                             <div className="metric-icon me-3" style={{
//                                 background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
//                                 width: '48px',
//                                 height: '48px'
//                             }}>
//                                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
//                                     <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                 </svg>
//                             </div>
//                             <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Snabbåtgärder</h5>
//                         </div>

//                         <div className="quick-actions">
//                             <Link to={`/${companyId}/create-invoice`} className="action-button">
//                                 <div className="action-icon" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}>
//                                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
//                                         <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                     </svg>
//                                 </div>
//                                 Skapa faktura
//                             </Link>
//                             <Link to={`/${companyId}/create-quote`} className="action-button">
//                                 <div className="action-icon" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }}>
//                                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
//                                         <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                     </svg>
//                                 </div>
//                                 Skapa offert
//                             </Link>
//                             <Link to={`/${companyId}/create-bookkeeping-entry`} className="action-button">
//                                 <div className="action-icon" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
//                                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
//                                         <path d="M4 19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                         <path d="M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                     </svg>
//                                 </div>
//                                 Bokför transaktion
//                             </Link>
//                             <Link to={`/${companyId}/customers`} className="action-button">
//                                 <div className="action-icon" style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)' }}>
//                                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#4338ca' }}>
//                                         <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                         <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
//                                     </svg>
//                                 </div>
//                                 Hantera kunder
//                             </Link>
//                         </div>
//                     </div>

//                     <div className="dashboard-section">
//                         <div className="d-flex align-items-center mb-4">
//                             <div className="metric-icon me-3" style={{
//                                 background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
//                                 width: '48px',
//                                 height: '48px'
//                             }}>
//                                 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
//                                     <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
//                                     <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                 </svg>
//                             </div>
//                             <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Kommande förfall</h5>
//                         </div>
//                         <UpcomingPayments />
//                     </div>
//                 </Col>
//             </Row>
//         </div>
//     );
// };

// export default Home;



// import React, { useContext, useEffect, useState } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Dropdown, Card, Row, Col, Spinner } from 'react-bootstrap';

// //Import Components
// import { ThemeContext } from "../../../context/ThemeContext";
// import axiosInstance from '../../../services/AxiosInstance';
// import { getApiUrl } from '../../../utils/ApiUtils';

// // Import new dashboard components
// import RevenueChart from './Dashboard/RevenueChart';
// import ExpenseChart from './Dashboard/ExpenseChart';
// import CashFlowChart from './Dashboard/CashFlowChart';
// import TopCustomers from './Dashboard/TopCustomers';
// import RecentTransactions from './Dashboard/RecentTransactions';
// import UpcomingPayments from './Dashboard/UpcomingPayments';

// const Home = () => {
// 	const { companyId } = useParams();
// 	const { changeBackground } = useContext(ThemeContext);
// 	const [loading, setLoading] = useState(true);
// 	const [dashboardData, setDashboardData] = useState({
// 		overview: {
// 			totalRevenue: 0,
// 			totalExpenses: 0,
// 			netProfit: 0,
// 			cashFlow: 0
// 		},
// 		invoices: {
// 			sent: 0,
// 			paid: 0,
// 			unpaid: 0,
// 			overdue: 0,
// 			totalOutstanding: 0
// 		},
// 		quotes: {
// 			total: 0,
// 			pending: 0,
// 			accepted: 0,
// 			rejected: 0
// 		},
// 		recentActivity: []
// 	});

// 	useEffect(() => {
// 		changeBackground({ value: "light", label: "Light" });
// 		fetchDashboardData();
// 	}, [companyId]);

// 	const fetchDashboardData = async () => {
// 		setLoading(true);
// 		try {
// 			// Use dummy data for now
// 			setDashboardData({
// 				overview: {
// 					totalRevenue: 1250000,
// 					totalExpenses: 890000,
// 					netProfit: 360000,
// 					cashFlow: 180000
// 				},
// 				invoices: {
// 					sent: 24,
// 					paid: 18,
// 					unpaid: 6,
// 					overdue: 2,
// 					totalOutstanding: 85000
// 				},
// 				quotes: {
// 					total: 12,
// 					pending: 5,
// 					accepted: 6,
// 					rejected: 1
// 				},
// 				recentActivity: []
// 			});
// 		} catch (error) {
// 			console.error('Error fetching dashboard data:', error);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	const formatCurrency = (amount) => {
// 		return new Intl.NumberFormat('sv-SE', {
// 			style: 'currency',
// 			currency: 'SEK',
// 			minimumFractionDigits: 0,
// 			maximumFractionDigits: 0
// 		}).format(amount);
// 	};

// 	if (loading) {
// 		return (
// 			<div className="container-fluid" style={{ maxWidth: '1400px' }}>
// 				<style>{`
//                     .loading-container {
//                         display: flex;
//                         justify-content: center;
//                         align-items: center;
//                         height: 400px;
//                         background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
//                         border-radius: 16px;
//                         border: 1px solid #e2e8f0;
//                     }
                    
//                     .loading-spinner {
//                         display: flex;
//                         flex-direction: column;
//                         align-items: center;
//                         gap: 1rem;
//                     }
                    
//                     .spinner-custom {
//                         width: 48px;
//                         height: 48px;
//                         border: 4px solid #e2e8f0;
//                         border-top: 4px solid #3b82f6;
//                         border-radius: 50%;
//                         animation: spin 1s linear infinite;
//                     }
                    
//                     @keyframes spin {
//                         0% { transform: rotate(0deg); }
//                         100% { transform: rotate(360deg); }
//                     }
//                 `}</style>
// 				<div className="loading-container">
// 					<div className="loading-spinner">
// 						<div className="spinner-custom"></div>
// 						<span style={{ color: '#64748b', fontSize: '0.875rem' }}>Laddar dashboard...</span>
// 					</div>
// 				</div>
// 			</div>
// 		);
// 	}

// 	return (
// 		<div className="container-fluid" style={{ maxWidth: '1400px' }}>
// 			<style>{`
//                 .dashboard-section {
//                     background: white;
//                     border-radius: 16px;
//                     padding: 2rem;
//                     margin-bottom: 2rem;
//                     border: 1px solid #e2e8f0;
//                     box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
//                     transition: all 0.3s ease;
//                 }
                
//                 .dashboard-section:hover {
//                     box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
//                     transform: translateY(-2px);
//                 }
                
//                 .metric-card {
//                     background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
//                     border-radius: 16px;
//                     padding: 2rem;
//                     border: 1px solid #e2e8f0;
//                     height: 100%;
//                     transition: all 0.3s ease;
//                     position: relative;
//                     overflow: hidden;
//                 }
                
//                 .metric-card::before {
//                     content: '';
//                     position: absolute;
//                     top: 0;
//                     left: 0;
//                     right: 0;
//                     height: 4px;
//                     background: var(--accent-gradient);
//                     transform: scaleX(0);
//                     transition: transform 0.3s ease;
//                     transform-origin: left;
//                 }
                
//                 .metric-card:hover::before {
//                     transform: scaleX(1);
//                 }
                
//                 .metric-card:hover {
//                     transform: translateY(-4px);
//                     box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
//                 }
                
//                 .metric-card.revenue {
//                     --accent-gradient: linear-gradient(90deg, #10b981 0%, #059669 100%);
//                 }
                
//                 .metric-card.expense {
//                     --accent-gradient: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
//                 }
                
//                 .metric-card.profit {
//                     --accent-gradient: linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%);
//                 }
                
//                 .metric-card.cashflow {
//                     --accent-gradient: linear-gradient(90deg, #06b6d4 0%, #0891b2 100%);
//                 }
                
//                 .metric-icon {
//                     width: 56px;
//                     height: 56px;
//                     border-radius: 16px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     margin-bottom: 1.5rem;
//                     transition: all 0.3s ease;
//                 }
                
//                 .metric-card:hover .metric-icon {
//                     transform: scale(1.1);
//                 }
                
//                 .metric-value {
//                     font-size: 2rem;
//                     font-weight: 700;
//                     line-height: 1.2;
//                     margin-bottom: 0.5rem;
//                     letter-spacing: -0.025em;
//                 }
                
//                 .metric-label {
//                     font-size: 0.875rem;
//                     color: #64748b;
//                     font-weight: 500;
//                     margin-bottom: 1rem;
//                 }
                
//                 .metric-change {
//                     font-size: 0.75rem;
//                     font-weight: 600;
//                     padding: 0.25rem 0.75rem;
//                     border-radius: 8px;
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 0.375rem;
//                 }
                
//                 .metric-change.positive {
//                     background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
//                     color: #166534;
//                 }
                
//                 .metric-change.negative {
//                     background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
//                     color: #dc2626;
//                 }
                
//                 .status-grid {
//                     display: grid;
//                     grid-template-columns: repeat(2, 1fr);
//                     gap: 1.5rem;
//                     margin-bottom: 2rem;
//                 }
                
//                 .status-item {
//                     background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
//                     border-radius: 12px;
//                     padding: 1.5rem;
//                     text-align: center;
//                     border: 1px solid #e2e8f0;
//                     transition: all 0.3s ease;
//                 }
                
//                 .status-item:hover {
//                     transform: translateY(-2px);
//                     box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
//                 }
                
//                 .status-value {
//                     font-size: 1.75rem;
//                     font-weight: 700;
//                     margin-bottom: 0.5rem;
//                     line-height: 1;
//                 }
                
//                 .status-label {
//                     font-size: 0.875rem;
//                     color: #64748b;
//                     font-weight: 500;
//                 }
                
//                 .chart-container {
//                     background: white;
//                     border-radius: 16px;
//                     padding: 2rem;
//                     border: 1px solid #e2e8f0;
//                     box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
//                     height: 100%;
//                 }
                
//                 .chart-header {
//                     display: flex;
//                     justify-content: between;
//                     align-items: center;
//                     margin-bottom: 2rem;
//                     padding-bottom: 1rem;
//                     border-bottom: 2px solid #f1f5f9;
//                 }
                
//                 .chart-title {
//                     font-size: 1.25rem;
//                     font-weight: 600;
//                     color: #1e293b;
//                     margin: 0;
//                 }
                
//                 .quick-actions {
//                     display: grid;
//                     gap: 1rem;
//                 }
                
//                 .action-button {
//                     display: flex;
//                     align-items: center;
//                     gap: 0.75rem;
//                     padding: 1rem 1.5rem;
//                     background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
//                     border: 1px solid #e2e8f0;
//                     border-radius: 12px;
//                     text-decoration: none;
//                     color: #374151;
//                     font-weight: 500;
//                     transition: all 0.3s ease;
//                     position: relative;
//                     overflow: hidden;
//                 }
                
//                 .action-button::before {
//                     content: '';
//                     position: absolute;
//                     top: 0;
//                     left: -100%;
//                     width: 100%;
//                     height: 100%;
//                     background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
//                     transition: left 0.5s ease;
//                 }
                
//                 .action-button:hover::before {
//                     left: 100%;
//                 }
                
//                 .action-button:hover {
//                     transform: translateY(-2px);
//                     box-shadow: 0 8px 20px rgba(59, 130, 246, 0.15);
//                     border-color: #3b82f6;
//                     color: #3b82f6;
//                     text-decoration: none;
//                 }
                
//                 .action-icon {
//                     width: 20px;
//                     height: 20px;
//                     border-radius: 6px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                 }
                
//                 .summary-card {
//                     background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
//                     border-radius: 12px;
//                     padding: 1.5rem;
//                     border: 1px solid #e2e8f0;
//                     margin-top: 2rem;
//                 }
                
//                 .summary-row {
//                     display: flex;
//                     justify-content: between;
//                     align-items: center;
//                     padding: 0.75rem 0;
//                 }
                
//                 .summary-row:not(:last-child) {
//                     border-bottom: 1px solid #e2e8f0;
//                 }
                
//                 .summary-label {
//                     color: #64748b;
//                     font-size: 0.875rem;
//                     font-weight: 500;
//                 }
                
//                 .summary-value {
//                     font-weight: 600;
//                     font-size: 0.875rem;
//                 }
//             `}</style>

// 			{/* Financial Overview Cards */}
// 			<Row className="mb-4">
// 				<Col xl={3} sm={6} className="mb-3">
// 					<div className="metric-card revenue">
// 						<div className="metric-icon" style={{
// 							background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)'
// 						}}>
// 							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
// 								<path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 								<path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 								<path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 						</div>
// 						<div className="metric-label">Total intäkter</div>
// 						<div className="metric-value" style={{ color: '#059669' }}>
// 							{formatCurrency(dashboardData.overview.totalRevenue)}
// 						</div>
// 						<div className="metric-change positive">
// 							<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// 								<path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 							+8.2% från förra månaden
// 						</div>
// 					</div>
// 				</Col>

// 				<Col xl={3} sm={6} className="mb-3">
// 					<div className="metric-card expense">
// 						<div className="metric-icon" style={{
// 							background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)'
// 						}}>
// 							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
// 								<path d="M17 14L12 19L7 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 								<path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 						</div>
// 						<div className="metric-label">Total kostnader</div>
// 						<div className="metric-value" style={{ color: '#dc2626' }}>
// 							{formatCurrency(dashboardData.overview.totalExpenses)}
// 						</div>
// 						<div className="metric-change negative">
// 							<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// 								<path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 							+3.1% från förra månaden
// 						</div>
// 					</div>
// 				</Col>

// 				<Col xl={3} sm={6} className="mb-3">
// 					<div className="metric-card profit">
// 						<div className="metric-icon" style={{
// 							background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
// 						}}>
// 							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
// 								<path d="M16 21V5C16 4.44772 15.5523 4 15 4H9C8.44772 4 8 4.44772 8 5V21L12 17L16 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 						</div>
// 						<div className="metric-label">Nettovinst</div>
// 						<div className="metric-value" style={{ color: '#1d4ed8' }}>
// 							{formatCurrency(dashboardData.overview.netProfit)}
// 						</div>
// 						<div className="metric-change positive">
// 							<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// 								<path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 							+12.4% från förra månaden
// 						</div>
// 					</div>
// 				</Col>

// 				<Col xl={3} sm={6} className="mb-3">
// 					<div className="metric-card cashflow">
// 						<div className="metric-icon" style={{
// 							background: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)'
// 						}}>
// 							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#0891b2' }}>
// 								<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
// 								<path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 						</div>
// 						<div className="metric-label">Kassaflöde</div>
// 						<div className="metric-value" style={{ color: '#0891b2' }}>
// 							{formatCurrency(dashboardData.overview.cashFlow)}
// 						</div>
// 						<div className="metric-change positive">
// 							<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// 								<path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 							</svg>
// 							+5.7% från förra månaden
// 						</div>
// 					</div>
// 				</Col>
// 			</Row>

// 			{/* Invoice & Quote Status */}
// 			<Row className="mb-4">
// 				<Col xl={6}>
// 					<div className="dashboard-section">
// 						<div className="d-flex align-items-center mb-4">
// 							<div className="metric-icon me-3" style={{
// 								background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
// 								width: '48px',
// 								height: '48px'
// 							}}>
// 								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
// 									<path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 									<path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 								</svg>
// 							</div>
// 							<h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Fakturor</h5>
// 						</div>

// 						<div className="status-grid">
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#3b82f6' }}>{dashboardData.invoices.sent}</div>
// 								<div className="status-label">Skickade</div>
// 							</div>
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#10b981' }}>{dashboardData.invoices.paid}</div>
// 								<div className="status-label">Betalda</div>
// 							</div>
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#f59e0b' }}>{dashboardData.invoices.unpaid}</div>
// 								<div className="status-label">Obetalda</div>
// 							</div>
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#ef4444' }}>{dashboardData.invoices.overdue}</div>
// 								<div className="status-label">Förfallna</div>
// 							</div>
// 						</div>

// 						<div className="summary-card">
// 							<div className="summary-row">
// 								<span className="summary-label">Utestående belopp:</span>
// 								<strong className="summary-value" style={{ color: '#f59e0b' }}>
// 									{formatCurrency(dashboardData.invoices.totalOutstanding)}
// 								</strong>
// 							</div>
// 						</div>
// 					</div>
// 				</Col>

// 				<Col xl={6}>
// 					<div className="dashboard-section">
// 						<div className="d-flex align-items-center mb-4">
// 							<div className="metric-icon me-3" style={{
// 								background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
// 								width: '48px',
// 								height: '48px'
// 							}}>
// 								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
// 									<path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 									<path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3C7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
// 								</svg>
// 							</div>
// 							<h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Offerter</h5>
// 						</div>

// 						<div className="status-grid">
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#3b82f6' }}>{dashboardData.quotes.total}</div>
// 								<div className="status-label">Totalt</div>
// 							</div>
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#f59e0b' }}>{dashboardData.quotes.pending}</div>
// 								<div className="status-label">Väntande</div>
// 							</div>
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#10b981' }}>{dashboardData.quotes.accepted}</div>
// 								<div className="status-label">Accepterade</div>
// 							</div>
// 							<div className="status-item">
// 								<div className="status-value" style={{ color: '#ef4444' }}>{dashboardData.quotes.rejected}</div>
// 								<div className="status-label">Avslagna</div>
// 							</div>
// 						</div>

// 						<div className="summary-card">
// 							<div className="summary-row">
// 								<span className="summary-label">Konverteringsgrad:</span>
// 								<strong className="summary-value" style={{ color: '#10b981' }}>
// 									{dashboardData.quotes.total > 0 ?
// 										Math.round((dashboardData.quotes.accepted / dashboardData.quotes.total) * 100) : 0}%
// 								</strong>
// 							</div>
// 						</div>
// 					</div>
// 				</Col>
// 			</Row>

// 			{/* Charts Row */}
// 			<Row className="mb-4">
// 				<Col xl={8}>
// 					<div className="chart-container">
// 						<div className="chart-header">
// 							<h5 className="chart-title">Intäkter vs Kostnader</h5>
// 							<Dropdown>
// 								<Dropdown.Toggle
// 									variant="outline-secondary"
// 									size="sm"
// 									style={{
// 										borderRadius: '8px',
// 										border: '1px solid #e2e8f0',
// 										color: '#64748b'
// 									}}
// 								>
// 									Senaste 12 månaderna
// 								</Dropdown.Toggle>
// 								<Dropdown.Menu>
// 									<Dropdown.Item>Senaste 6 månaderna</Dropdown.Item>
// 									<Dropdown.Item>Senaste 12 månaderna</Dropdown.Item>
// 									<Dropdown.Item>Detta år</Dropdown.Item>
// 								</Dropdown.Menu>
// 							</Dropdown>
// 						</div>
// 						<RevenueChart />
// 					</div>
// 				</Col>

// 				<Col xl={4}>
// 					<div className="chart-container">
// 						<div className="chart-header">
// 							<h5 className="chart-title">Kostnadsfördelning</h5>
// 						</div>
// 						<ExpenseChart />
// 					</div>
// 				</Col>
// 			</Row>

// 			{/* Recent Activity & Quick Actions */}
// 			<Row>
// 				<Col xl={8}>
// 					<div className="chart-container">
// 						<div className="chart-header">
// 							<h5 className="chart-title">Senaste aktivitet</h5>
// 							<Link
// 								to={`/${companyId}/verifications`}
// 								className="action-button"
// 								style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
// 							>
// 								Visa alla transaktioner
// 							</Link>
// 						</div>
// 						<RecentTransactions />
// 					</div>
// 				</Col>

// 				<Col xl={4}>
// 					<div className="dashboard-section mb-4">
// 						<div className="d-flex align-items-center mb-4">
// 							<div className="metric-icon me-3" style={{
// 								background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
// 								width: '48px',
// 								height: '48px'
// 							}}>
// 								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
// 									<path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 								</svg>
// 							</div>
// 							<h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Snabbåtgärder</h5>
// 						</div>

// 						<div className="quick-actions">
// 							<Link to={`/${companyId}/create-invoice`} className="action-button">
// 								<div className="action-icon" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }}>
// 									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#1d4ed8' }}>
// 										<path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 									</svg>
// 								</div>
// 								Skapa faktura
// 							</Link>
// 							<Link to={`/${companyId}/create-quote`} className="action-button">
// 								<div className="action-icon" style={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }}>
// 									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#059669' }}>
// 										<path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 									</svg>
// 								</div>
// 								Skapa offert
// 							</Link>
// 							<Link to={`/${companyId}/create-bookkeeping-entry`} className="action-button">
// 								<div className="action-icon" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
// 									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#d97706' }}>
// 										<path d="M4 19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 										<path d="M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 									</svg>
// 								</div>
// 								Bokför transaktion
// 							</Link>
// 							<Link to={`/${companyId}/customers`} className="action-button">
// 								<div className="action-icon" style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)' }}>
// 									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#4338ca' }}>
// 										<path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 										<circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
// 										<path d="M23 21V19C23 18.1645 22.7155 17.3541 22.2094 16.7107C21.7033 16.0673 20.0075 15.6228 19 15.3949" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 										<path d="M16 3.13C17.0013 3.35715 17.6936 3.80298 18.2094 4.44561C18.7155 5.08825 19 5.89435 19 6.72507C19 7.55579 18.7155 8.36189 18.2094 9.00453C17.7033 9.64716 17.0075 10.093 16 10.32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 									</svg>
// 								</div>
// 								Hantera kunder
// 							</Link>
// 						</div>
// 					</div>

// 					<div className="dashboard-section">
// 						<div className="d-flex align-items-center mb-4">
// 							<div className="metric-icon me-3" style={{
// 								background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
// 								width: '48px',
// 								height: '48px'
// 							}}>
// 								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
// 									<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
// 									<path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
// 								</svg>
// 							</div>
// 							<h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>Kommande förfall</h5>
// 						</div>
// 						<UpcomingPayments />
// 					</div>
// 				</Col>
// 			</Row>
// 		</div>
// 	);
// };

// export default Home;


// // import React, { useContext, useEffect } from 'react';
// // import { Link } from 'react-router-dom';
// // import loadable from "@loadable/component";
// // import pMinDelay from "p-min-delay";
// // import { Dropdown } from 'react-bootstrap';

// // ///Images
// // import small from "./../../../images/profile/small/pic1.jpg";
// // import avt1 from "./../../../images/avatar/1.jpg";
// // import avt2 from "./../../../images/avatar/2.jpg";
// // import avt3 from "./../../../images/avatar/3.jpg";
// // import avt4 from "./../../../images/avatar/4.jpg";
// // import avt5 from "./../../../images/avatar/5.jpg";
// // import avt6 from "./../../../images/avatar/6.jpg";


// // //Import Components
// // import { ThemeContext } from "../../../context/ThemeContext";

// // import DonutChart from './Dashboard/DonutChart';
// // import WidgetChart3 from './Dashboard/WidgetChart3';
// // import PreviousTransactions from './Dashboard/PreviousTransactions';
// // import NouiRangeSlider from './Dashboard/NouiRangeSlider';

// // const TotalInvoices = loadable(() =>
// // 	pMinDelay(import("./Dashboard/TotalInvoices"), 1000)
// // );
// // const Paidinvoices = loadable(() =>
// // 	pMinDelay(import("./Dashboard/Paidinvoices"), 1000)
// // );
// // const Unpaidinvoices = loadable(() =>
// // 	pMinDelay(import("./Dashboard/Unpaidinvoices"), 1000)
// // );
// // const Totalinvoicessent = loadable(() =>
// // 	pMinDelay(import("./Dashboard/Totalinvoicessent"), 1000)
// // );
// // const ChartBarApex = loadable(() =>
// // 	pMinDelay(import("./Dashboard/ChartBarApex"), 1000)
// // );


// // const Home = () => {
// // 	const { changeBackground } = useContext(ThemeContext);
// // 	useEffect(() => {
// // 		changeBackground({ value: "light", label: "Light" });
// // 	}, []);
// // 	return (
// // 		<>
// // 			<div className="row">
// // 				<div className="col-xl-12">
// // 					<div className="row">
// // 						<div className="col-xl-3 col-sm-6">
// // 							<div className="card overflow-hidden">
// // 								<div className="card-header border-0">
// // 									<div className="d-flex">
// // 										<span className="mt-2">
// // 											<svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 												<path fillRule="evenodd" clipRule="evenodd" d="M9.812 34.64L3.2 39.6C2.594 40.054 1.784 40.128 1.106 39.788C0.428 39.45 0 38.758 0 38V2C0 0.896 0.896 0 2 0H30C31.104 0 32 0.896 32 2V38C32 38.758 31.572 39.45 30.894 39.788C30.216 40.128 29.406 40.054 28.8 39.6L22.188 34.64L17.414 39.414C16.634 40.196 15.366 40.196 14.586 39.414L9.812 34.64ZM28 34V4H4V34L8.8 30.4C9.596 29.802 10.71 29.882 11.414 30.586L16 35.172L20.586 30.586C21.29 29.882 22.404 29.802 23.2 30.4L28 34ZM14 20H18C19.104 20 20 19.104 20 18C20 16.896 19.104 16 18 16H14C12.896 16 12 16.896 12 18C12 19.104 12.896 20 14 20ZM10 12H22C23.104 12 24 11.104 24 10C24 8.896 23.104 8 22 8H10C8.896 8 8 8.896 8 10C8 11.104 8.896 12 10 12Z" fill="#717579" />
// // 											</svg>
// // 										</span>
// // 										<div className="invoices">
// // 											<h4>2,478</h4>
// // 											<span>Total Invoices</span>
// // 										</div>
// // 									</div>
// // 								</div>
// // 								<div className="card-body p-0">
// // 									<div id="totalInvoices">
// // 										<TotalInvoices />
// // 									</div>
// // 								</div>

// // 							</div>
// // 						</div>
// // 						<div className="col-xl-3 col-sm-6">
// // 							<div className="card overflow-hidden">
// // 								<div className="card-header border-0">
// // 									<div className="d-flex">
// // 										<span className="mt-1">
// // 											<svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 												<path fillRule="evenodd" clipRule="evenodd" d="M17.812 48.64L11.2 53.6C10.594 54.054 9.78401 54.128 9.10602 53.788C8.42802 53.45 8.00002 52.758 8.00002 52V16C8.00002 14.896 8.89602 14 10 14H38C39.104 14 40 14.896 40 16V52C40 52.758 39.572 53.45 38.894 53.788C38.216 54.128 37.406 54.054 36.8 53.6L30.188 48.64L25.414 53.414C24.634 54.196 23.366 54.196 22.586 53.414L17.812 48.64ZM36 48V18H12V48L16.8 44.4C17.596 43.802 18.71 43.882 19.414 44.586L24 49.172L28.586 44.586C29.29 43.882 30.404 43.802 31.2 44.4L36 48ZM22 34H26C27.104 34 28 33.104 28 32C28 30.896 27.104 30 26 30H22C20.896 30 20 30.896 20 32C20 33.104 20.896 34 22 34ZM18 26H30C31.104 26 32 25.104 32 24C32 22.896 31.104 22 30 22H18C16.896 22 16 22.896 16 24C16 25.104 16.896 26 18 26Z" fill="#44814E" />
// // 												<circle cx="43.5" cy="14.5" r="12.5" fill="#09BD3C" stroke="white" strokeWidth="4" />
// // 											</svg>
// // 										</span>
// // 										<div className="invoices">
// // 											<h4>983</h4>
// // 											<span>Paid Invoices</span>
// // 										</div>
// // 									</div>
// // 								</div>
// // 								<div className="card-body p-0">
// // 									<div id="paidinvoices">
// // 										<Paidinvoices />
// // 									</div>
// // 								</div>

// // 							</div>
// // 						</div>
// // 						<div className="col-xl-3 col-sm-6">
// // 							<div className="card overflow-hidden">
// // 								<div className="card-header border-0">
// // 									<div className="d-flex">
// // 										<span className="mt-1">
// // 											<svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 												<path fillRule="evenodd" clipRule="evenodd" d="M17.812 48.64L11.2 53.6C10.594 54.054 9.78401 54.128 9.10602 53.788C8.42802 53.45 8.00002 52.758 8.00002 52V16C8.00002 14.896 8.89602 14 10 14H38C39.104 14 40 14.896 40 16V52C40 52.758 39.572 53.45 38.894 53.788C38.216 54.128 37.406 54.054 36.8 53.6L30.188 48.64L25.414 53.414C24.634 54.196 23.366 54.196 22.586 53.414L17.812 48.64ZM36 48V18H12V48L16.8 44.4C17.596 43.802 18.71 43.882 19.414 44.586L24 49.172L28.586 44.586C29.29 43.882 30.404 43.802 31.2 44.4L36 48ZM22 34H26C27.104 34 28 33.104 28 32C28 30.896 27.104 30 26 30H22C20.896 30 20 30.896 20 32C20 33.104 20.896 34 22 34ZM18 26H30C31.104 26 32 25.104 32 24C32 22.896 31.104 22 30 22H18C16.896 22 16 22.896 16 24C16 25.104 16.896 26 18 26Z" fill="#44814E" />
// // 												<circle cx="43.5" cy="14.5" r="12.5" fill="#FD5353" stroke="white" strokeWidth="4" />
// // 											</svg>

// // 										</span>
// // 										<div className="invoices">
// // 											<h4>1,256</h4>
// // 											<span>Unpaid Invoices</span>
// // 										</div>
// // 									</div>
// // 								</div>
// // 								<div className="card-body p-0">
// // 									<div id="unpaidinvoices">
// // 										<Unpaidinvoices />
// // 									</div>
// // 								</div>

// // 							</div>
// // 						</div>
// // 						<div className="col-xl-3 col-sm-6">
// // 							<div className="card overflow-hidden">
// // 								<div className="card-header border-0">
// // 									<div className="d-flex">
// // 										<span className="mt-1">
// // 											<svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 												<path fillRule="evenodd" clipRule="evenodd" d="M17.812 48.64L11.2 53.6C10.594 54.054 9.784 54.128 9.106 53.788C8.428 53.45 8 52.758 8 52V16C8 14.896 8.896 14 10 14H38C39.104 14 40 14.896 40 16V52C40 52.758 39.572 53.45 38.894 53.788C38.216 54.128 37.406 54.054 36.8 53.6L30.188 48.64L25.414 53.414C24.634 54.196 23.366 54.196 22.586 53.414L17.812 48.64ZM36 48V18H12V48L16.8 44.4C17.596 43.802 18.71 43.882 19.414 44.586L24 49.172L28.586 44.586C29.29 43.882 30.404 43.802 31.2 44.4L36 48ZM22 34H26C27.104 34 28 33.104 28 32C28 30.896 27.104 30 26 30H22C20.896 30 20 30.896 20 32C20 33.104 20.896 34 22 34ZM18 26H30C31.104 26 32 25.104 32 24C32 22.896 31.104 22 30 22H18C16.896 22 16 22.896 16 24C16 25.104 16.896 26 18 26Z" fill="#44814E" />
// // 												<circle cx="43.5" cy="14.5" r="12.5" fill="#FFAA2B" stroke="white" strokeWidth="4" />
// // 											</svg>


// // 										</span>
// // 										<div className="invoices">
// // 											<h4>652</h4>
// // 											<span>Total Invoices</span>
// // 										</div>
// // 									</div>
// // 								</div>
// // 								<div className="card-body p-0">
// // 									<div id="totalinvoicessent">
// // 										<Totalinvoicessent />
// // 									</div>
// // 								</div>
// // 							</div>
// // 						</div>
// // 					</div>
// // 				</div>
// // 				<div className="col-xl-6">
// // 					<div className="row">
// // 						<div className="col-xl-12">
// // 							<div className="card">
// // 								<div className="card-body">
// // 									<div className="row">
// // 										<div className="col-xl-5 col-xxl-12 col-md-5">
// // 											<h4 className="fs-20 text-black mb-4 font-w700">Spendings</h4>
// // 											<div className="row">
// // 												<div className="d-flex col-xl-12 col-xxl-6  col-md-12 col-6 mb-4">
// // 													<svg className="me-3" width="14" height="54" viewBox="0 0 14 54" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 														<rect x="-6.10352e-05" width="14" height="54" rx="7" fill="#AC39D4" />
// // 													</svg>
// // 													<div>
// // 														<p className="fs-14 mb-2">Investment</p>
// // 														<span className="fs-16 font-w600 text-light"><span className="text-black me-2 font-w700">$1,567</span>/$5,000</span>
// // 													</div>
// // 												</div>
// // 												<div className="d-flex col-xl-12 col-xxl-6 col-md-12 col-6 mb-4">
// // 													<svg className="me-3" width="14" height="54" viewBox="0 0 14 54" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 														<rect x="-6.10352e-05" width="14" height="54" rx="7" fill="#40D4A8" />
// // 													</svg>
// // 													<div>
// // 														<p className="fs-14 mb-2">Installment</p>
// // 														<span className="fs-16 font-w600 text-light"><span className="text-black me-2 font-w700">$1,567</span>/$5,000</span>
// // 													</div>
// // 												</div>
// // 												<div className="d-flex col-xl-12 col-xxl-6 col-md-12 col-6 mb-4">
// // 													<svg className="me-3" width="14" height="54" viewBox="0 0 14 54" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 														<rect x="-6.10352e-05" width="14" height="54" rx="7" fill="#1EB6E7" />
// // 													</svg>
// // 													<div>
// // 														<p className="fs-14 mb-2">Restaurant</p>
// // 														<span className="fs-16 font-w600 text-light"><span className="text-black me-2 font-w700">$1,567</span>/$5,000</span>
// // 													</div>
// // 												</div>
// // 												<div className="d-flex col-xl-12 col-xxl-6 col-md-12 col-6 mb-4">
// // 													<svg className="me-3" width="14" height="54" viewBox="0 0 14 54" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 														<rect x="-6.10352e-05" width="14" height="54" rx="7" fill="#461EE7" />
// // 													</svg>
// // 													<div>
// // 														<p className="fs-14 mb-2">Property</p>
// // 														<span className="fs-16 font-w600 text-light"><span className="text-black me-2 font-w700">$1,567</span>/$5,000</span>
// // 													</div>
// // 												</div>
// // 											</div>
// // 										</div>
// // 										<div className="col-xl-7  col-xxl-12 col-md-7">
// // 											<div className="row">
// // 												<div className="col-sm-6 mb-4">
// // 													<div className="bg-gradient1 rounded text-center p-3">
// // 														<div className="d-inline-block position-relative donut-chart-sale mb-3">
// // 															<DonutChart value="60" backgroundColor="rgba(255, 255, 255,1)"
// // 																backgroundColor2="rgba(255, 255, 255, 0.2)"
// // 															/>
// // 															<small className="text-white">71%</small>
// // 														</div>
// // 														<span className="fs-14 text-white d-block">Investment</span>
// // 													</div>
// // 												</div>
// // 												<div className="col-sm-6 mb-4">
// // 													<div className="bg-gradient2 rounded text-center p-3">
// // 														<div className="d-inline-block position-relative donut-chart-sale mb-3">
// // 															<DonutChart value="40" backgroundColor="rgba(255, 255, 255,1)"
// // 																backgroundColor2="rgba(255, 255, 255, 0.2)"
// // 															/>
// // 															<small className="text-white">30%</small>
// // 														</div>
// // 														<span className="fs-14 text-white d-block">Installment</span>
// // 													</div>
// // 												</div>
// // 												<div className="col-sm-6 mb-sm-0 mb-4">
// // 													<div className="rounded text-center p-3 bg-gradient3">
// // 														<div className="d-inline-block position-relative donut-chart-sale mb-3">
// // 															<DonutChart value="12" backgroundColor="rgba(255, 255, 255,1)"
// // 																backgroundColor2="rgba(255, 255, 255, 0.2)"
// // 															/>
// // 															<small className="text-white">5%</small>
// // 														</div>
// // 														<span className="fs-14 text-white d-block">Restaurant</span>
// // 													</div>
// // 												</div>
// // 												<div className="col-sm-6 mb-sm-0 mb-4">
// // 													<div className="rounded text-center p-3 bg-gradient4">
// // 														<div className="d-inline-block position-relative donut-chart-sale mb-3">
// // 															<DonutChart value="90" backgroundColor="rgba(255, 255, 255,1)"
// // 																backgroundColor2="rgba(255, 255, 255, 0.2)"
// // 															/>
// // 															<small className="text-white">96%</small>
// // 														</div>
// // 														<span className="fs-14 text-white d-block">Property</span>
// // 													</div>
// // 												</div>
// // 											</div>
// // 										</div>
// // 									</div>
// // 								</div>
// // 							</div>
// // 						</div>
// // 						<div className="col-xl-12">
// // 							<div className="card">
// // 								<div className="card-header d-sm-flex d-block border-0 pb-0">
// // 									<div className="pe-3 me-auto mb-sm-0 mb-3">
// // 										<h4 className="fs-20 text-black mb-1 font-w700">Transaction Overview</h4>
// // 										<span className="fs-12">Lorem ipsum dolor sit amet, consectetur</span>
// // 									</div>
// // 									<div className="d-flex align-items-center justify-content-between">
// // 										<Link to={"#"} className="btn btn-outline-primary me-3"><i className="las la-download text-primary scale5 me-3"></i>Download Report</Link>
// // 										<Dropdown className="dropdown">
// // 											<Dropdown.Toggle as="div" className="btn-link i-false">
// // 												<svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1"><g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"><rect x="0" y="0" width="24" height="24"></rect><circle fill="#000000" cx="5" cy="12" r="2"></circle><circle fill="#000000" cx="12" cy="12" r="2"></circle><circle fill="#000000" cx="19" cy="12" r="2"></circle></g></svg>
// // 											</Dropdown.Toggle>
// // 											<Dropdown.Menu className="dropdown-menu dropdown-menu-right">
// // 												<Dropdown.Item className="dropdown-item">Delete</Dropdown.Item>
// // 												<Dropdown.Item className="dropdown-item">Edit</Dropdown.Item>
// // 											</Dropdown.Menu>
// // 										</Dropdown>
// // 									</div>
// // 								</div>
// // 								<div className="card-body">
// // 									<div id="chartBar" className="chartBar">
// // 										<ChartBarApex />
// // 									</div>
// // 									<div className="d-flex justify-content-between flex-wrap">
// // 										<div className="d-flex">
// // 											<label className="form-check-label font-w600 fs-16" htmlFor="flexSwitchCheckChecked1"
// // 											>Number
// // 											</label>
// // 											<div className="form-check form-switch toggle-switch">
// // 												<input className="form-check-input custome" type="checkbox"
// // 													id="flexSwitchCheckChecked1"
// // 													defaultChecked
// // 												/>
// // 											</div>
// // 											<label className="form-check-label font-w600 fs-16" htmlFor="flexSwitchCheckChecked2">Analytics</label>
// // 											<div className="form-check form-switch toggle-switch">
// // 												<input className="form-check-input custome" type="checkbox"
// // 													defaultChecked
// // 													id="flexSwitchCheckChecked2"
// // 												/>
// // 											</div>
// // 										</div>
// // 										<div>
// // 											<span className="fs-16 font-w600">
// // 												<svg className="me-2" width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 													<rect x="0.639771" width="18.9471" height="19" rx="9.47357" fill="#09BD3C" />
// // 												</svg>
// // 												Income
// // 											</span>
// // 											<span className="fs-16 font-w600">
// // 												<svg className="mx-2" width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 													<rect x="0.344925" width="18.9471" height="19" rx="9.47357" fill="#FD5353" />
// // 												</svg>
// // 												Expense
// // 											</span>
// // 										</div>
// // 									</div>
// // 								</div>
// // 							</div>
// // 						</div>
// // 						<div className="col-xl-12">
// // 							<div className="card overflow-hidden">
// // 								<div className="card-header d-sm-flex d-block border-0 pb-0">
// // 									<div className="mb-sm-0 mb-2">
// // 										<p className="fs-14 mb-1 font-w700">Weekly Wallet Usage</p>
// // 										<span className="mb-0">
// // 											<svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 												<path d="M11.9999 6L5.99994 -2.62268e-07L-6.10352e-05 6" fill="#2BC155" />
// // 											</svg>
// // 											<strong className="fs-32 text-black ms-2 me-3 font-w800">43%</strong>Than last week</span>
// // 									</div>
// // 									<span className="fs-12">
// // 										<svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-1">
// // 											<path d="M0.999939 13.5C1.91791 12.4157 4.89722 9.22772 6.49994 7.5L12.4999 10.5L19.4999 1.5" stroke="#2BC155" strokeWidth="2" />
// // 											<path d="M6.49994 7.5C4.89722 9.22772 1.91791 12.4157 0.999939 13.5H19.4999V1.5L12.4999 10.5L6.49994 7.5Z" fill="url(#paint0_linear2)" />
// // 											<defs>
// // 												<linearGradient id="paint0_linear2" x1="10.2499" y1="3" x2="10.9999" y2="13.5" gradientUnits="userSpaceOnUse">
// // 													<stop offset="0" stopColor="#2BC155" stopOpacity="0.73" />
// // 													<stop offset="1" stopColor="#2BC155" stopOpacity="0" />
// // 												</linearGradient>
// // 											</defs>
// // 										</svg>
// // 										4% (30 days)</span>
// // 								</div>
// // 								<div className="card-body p-0">
// // 									{/* <canvas id="widgetChart3" height="80"></canvas> */}
// // 									<WidgetChart3 />
// // 								</div>
// // 							</div>
// // 						</div>
// // 					</div>
// // 				</div>
// // 				<div className="col-xl-6">
// // 					<div className="row">
// // 						<div className="col-xl-12">
// // 							<div className="row">
// // 								<div className="col-xl-6 col-xxl-12 col-sm-6">
// // 									<div className="card">
// // 										<div className="card-header border-0 pb-0">
// // 											<div>
// // 												<h4 className="mb-2 font-w700">Quick Transfer</h4>
// // 												<span className="fs-12">Lorem ipsum dolor sit amet, consectetur</span>
// // 											</div>
// // 											<Dropdown className="dropdown">
// // 												<Dropdown.Toggle as="div" className="btn-link i-false">
// // 													<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 														<path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#575757" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
// // 														<path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="#575757" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
// // 														<path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="#575757" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
// // 													</svg>
// // 												</Dropdown.Toggle>
// // 												<Dropdown.Menu className="dropdown-menu dropdown-menu-right">
// // 													<Dropdown.Item className="dropdown-item">Delete</Dropdown.Item>
// // 													<Dropdown.Item className="dropdown-item">Edit</Dropdown.Item>
// // 												</Dropdown.Menu>
// // 											</Dropdown>
// // 										</div>
// // 										<div className="card-body">
// // 											<div className="user-bx">
// // 												<img src={small} alt="" />
// // 												<div>
// // 													<h6 className="user-name">Samuel</h6>
// // 													<span className="meta">@sam224</span>
// // 												</div>
// // 												<i className="las la-check-circle check-icon"></i>
// // 											</div>
// // 											<h4 className="mt-3 mb-3">Recent Friend<Link to={"#"} className="fs-16 float-end text-secondary font-w600">See More</Link></h4>
// // 											<ul className="user-list">
// // 												<li><img src={avt1} alt="" /></li>
// // 												<li><img src={avt2} alt="" /></li>
// // 												<li><img src={avt3} alt="" /></li>
// // 												<li><img src={avt4} alt="" /></li>
// // 												<li><img src={avt5} alt="" /></li>
// // 												<li><img src={avt6} alt="" /></li>
// // 											</ul>
// // 											<h4 className="mt-3 mb-0">Insert Amount</h4>
// // 											<div className="format-slider">
// // 												{/* <input className="form-control amount-input"  title="Formatted number" id="input-format"
// // 													placeholder="20.000"
// // 												/> */}
// // 												<div id="slider-format">
// // 													<NouiRangeSlider />
// // 												</div>

// // 											</div>
// // 											<div className="text-secondary fs-16 d-flex justify-content-between font-w600 mt-4">
// // 												<span>Your Balance</span>
// // 												<span>$ 456,345.62</span>
// // 											</div>
// // 										</div>
// // 										<div className="card-footer border-0 pt-0">
// // 											<Link to={"#"} className="btn btn-primary d-block btn-lg text-uppercase">Transfer Now</Link>
// // 										</div>
// // 									</div>
// // 								</div>
// // 								<div className="col-xl-6 col-xxl-12 col-sm-6">
// // 									<div className="card">
// // 										<div className="card-header border-0 pb-0">
// // 											<div>
// // 												<h4 className="mb-2 fs-20 font-w700">Bar Spendings</h4>
// // 												<span className="fs-12">Lorem ipsum dolor sit amet, consectetur</span>
// // 											</div>

// // 											<Dropdown className="dropdown">
// // 												<Dropdown.Toggle as="div" className="btn-link i-false">
// // 													<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
// // 														<path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#575757" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
// // 														<path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="#575757" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
// // 														<path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="#575757" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
// // 													</svg>
// // 												</Dropdown.Toggle>
// // 												<Dropdown.Menu className="dropdown-menu dropdown-menu-right">
// // 													<Dropdown.Item className="dropdown-item">Delete</Dropdown.Item>
// // 													<Dropdown.Item className="dropdown-item">Edit</Dropdown.Item>
// // 												</Dropdown.Menu>
// // 											</Dropdown>
// // 										</div>
// // 										<div className="card-body">
// // 											<div className="progress default-progress">
// // 												<div className="progress-bar bg-gradient1 progress-animated" style={{ width: "45%", height: "20px" }}>
// // 													<span className="sr-only">45% Complete</span>
// // 												</div>
// // 											</div>
// // 											<div className="d-flex align-items-end mt-2 pb-3 justify-content-between">
// // 												<span>Investment</span>
// // 												<span className="fs-16 font-w600 text-light"><span className="text-black pe-2">$1415</span>/$2000</span>
// // 											</div>
// // 											<div className="progress default-progress mt-4">
// // 												<div className="progress-bar bg-gradient2 progress-animated" style={{ width: "70%", height: "20px" }} >
// // 													<span className="sr-only">70% Complete</span>
// // 												</div>
// // 											</div>
// // 											<div className="d-flex align-items-end mt-2 pb-3 justify-content-between">
// // 												<span>Restaurant</span>
// // 												<span className="fs-16 font-w600 text-light"><span className="text-black pe-2">$1567</span>/$5000</span>
// // 											</div>
// // 											<div className="progress default-progress mt-4">
// // 												<div className="progress-bar bg-gradient4 progress-animated" style={{ width: "35%", height: "20px" }} >
// // 													<span className="sr-only">35% Complete</span>
// // 												</div>
// // 											</div>
// // 											<div className="d-flex align-items-end mt-2 pb-3 justify-content-between">
// // 												<span>Installment</span>
// // 												<span className="fs-16 font-w600 text-light"><span className="text-black pe-2">$487</span>/$10000</span>
// // 											</div>
// // 											<div className="progress default-progress mt-4">
// // 												<div className="progress-bar bg-gradient3 progress-animated" style={{ width: "95%", height: "20px" }}>
// // 													<span className="sr-only">95% Complete</span>
// // 												</div>
// // 											</div>
// // 											<div className="d-flex align-items-end mt-2 justify-content-between">
// // 												<span>Property</span>
// // 												<span className="fs-16 font-w600 text-light"><span className="text-black pe-2">$3890</span>/$4000</span>
// // 											</div>
// // 										</div>
// // 										<div className="card-footer border-0 pt-0">
// // 											<Link to={"#"} className="btn btn-outline-primary d-block btn-lg">View More</Link>
// // 										</div>
// // 									</div>
// // 								</div>

// // 							</div>
// // 						</div>
// // 						<div className="col-xl-12">
// // 							<PreviousTransactions />
// // 						</div>
// // 					</div>
// // 				</div>

// // 			</div>
// // 		</>
// // 	)
// // }
// // export default Home;